/* src/render/peopleGeometry.ts — the little people (and their cars), shared.
 *
 * One box-soup builder and three prototype geometries: a hatchback, a body, a head. The ambient
 * street life instances them across the city; the interior instances the same body and head as
 * shoppers inside an open shop — the same citizens outside and in, at the same scale.
 *
 * Bodies are authored white so a per-instance colour lands on the shirt; heads are authored
 * white and instance-tinted to skin, riding in their own mesh so a shirt colour never tints a
 * face. Legs, hair, glazing and wheels are baked dark and barely notice the tint.
 */

import { BufferAttribute, BufferGeometry } from 'three';

function rgb(hex: number): [number, number, number] {
  return [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];
}

class Proto {
  readonly pos: number[] = [];
  readonly nrm: number[] = [];
  readonly col: number[] = [];

  box(cx: number, cy: number, cz: number, sx: number, sy: number, sz: number, hex: number): void {
    const c = rgb(hex);
    const x0 = cx - sx / 2, x1 = cx + sx / 2;
    const y0 = cy - sy / 2, y1 = cy + sy / 2;
    const z0 = cz - sz / 2, z1 = cz + sz / 2;
    const quad = (
      a: readonly number[], b: readonly number[], cc: readonly number[], d: readonly number[],
      n: readonly number[],
    ): void => {
      for (const v of [a, b, cc, a, cc, d]) {
        this.pos.push(v[0] ?? 0, v[1] ?? 0, v[2] ?? 0);
        this.nrm.push(n[0] ?? 0, n[1] ?? 0, n[2] ?? 0);
        this.col.push(c[0], c[1], c[2]);
      }
    };
    quad([x0, y1, z0], [x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [0, 1, 0]);
    quad([x0, y0, z1], [x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [0, -1, 0]);
    quad([x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1], [0, 0, 1]);
    quad([x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0], [0, 0, -1]);
    quad([x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1], [1, 0, 0]);
    quad([x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0], [-1, 0, 0]);
  }

  toGeometry(): BufferGeometry {
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(new Float32Array(this.pos), 3));
    g.setAttribute('normal', new BufferAttribute(new Float32Array(this.nrm), 3));
    g.setAttribute('color', new BufferAttribute(new Float32Array(this.col), 3));
    return g;
  }
}

/** A hatchback: white body takes the per-instance tint; glazing and wheels are baked dark. */
export function carGeometry(): BufferGeometry {
  const p = new Proto();
  p.box(0, 0.42, 0, 3.9, 0.5, 1.72, 0xffffff);
  p.box(-0.2, 0.86, 0, 2.0, 0.46, 1.56, 0xf2f2f2);
  p.box(-0.2, 0.86, 0.001, 1.9, 0.34, 1.58, 0x2c343c);
  for (const sx of [1.25, -1.35]) {
    for (const sz of [0.82, -0.82]) {
      p.box(sx, 0.3, sz, 0.62, 0.6, 0.22, 0x1c1c20);
    }
  }
  return p.toGeometry();
}

/** Torso, legs and arms — white so the per-instance shirt colour lands on the lot. */
export function personBodyGeometry(): BufferGeometry {
  const p = new Proto();
  p.box(0, 1.05, 0, 0.4, 0.5, 0.24, 0xffffff);
  p.box(-0.1, 0.4, 0, 0.15, 0.8, 0.18, 0x3a4552);
  p.box(0.1, 0.4, 0, 0.15, 0.8, 0.18, 0x2f3844);
  p.box(-0.26, 1.02, 0, 0.11, 0.46, 0.16, 0xf0f0f0);
  p.box(0.26, 1.02, 0, 0.11, 0.46, 0.16, 0xf0f0f0);
  return p.toGeometry();
}

/** The head rides in its own instanced mesh so skin is skin whatever the shirt colour. */
export function personHeadGeometry(): BufferGeometry {
  const p = new Proto();
  p.box(0, 1.46, 0, 0.24, 0.26, 0.24, 0xffffff);
  p.box(0, 1.6, 0, 0.26, 0.06, 0.26, 0x3a3026);
  return p.toGeometry();
}

export const SHIRT_COLOURS = [
  0xc94f3f, 0x3f6fc9, 0x5a8a4a, 0xc9a23f, 0x7a4fc9, 0x2f8a8a, 0xd8d2c4, 0x33383f,
] as const;
export const SKIN = 0xe8c39e;
